import { memo, useEffect, useMemo } from 'react';

import { Autosave } from '@/components/core';
import { toOrganizationId, useAutoUpdateOrganization, usePutFile } from '@/hooks/api';
import { useOrganizationStore } from '@/store';
import { DivProps } from '@/utils';

import { SettingsHeader } from './SettingsHeader';

export type WorkspaceHeaderProps = DivProps;

const IWorkspaceHeader = ({ children, ...rest }: WorkspaceHeaderProps) => {
  const name = useOrganizationStore((state) => state.name);
  const planId = useOrganizationStore((state) => state.planId);
  const paymentMethodId = useOrganizationStore((state) => state.paymentMethodId);
  const pictureUrl = useOrganizationStore((state) => state.pictureUrl);
  const picture = useOrganizationStore((state) => state.picture);
  const documentRetentionInDays = useOrganizationStore((state) => state.documentRetentionInDays);

  const updates = useMemo(() => {
    const common = { name, documentRetentionInDays };
    if (paymentMethodId) {
      return { ...common, planId, paymentMethodId };
    } else {
      return common;
    }
  }, [name, documentRetentionInDays, paymentMethodId, planId]);
  const {
    isPending: isPendingUpdateOrganization,
    isSuccess: isSuccessUpdateOrganization,
    error: errorUpdateOrganization,
  } = useAutoUpdateOrganization(1000, toOrganizationId('me'), updates);
  const {
    mutate: updatePicture,
    isPending: isPendingUpdatePicture,
    isSuccess: isSuccessUpdatePicture,
    error: errorUpdatePicture,
  } = usePutFile();

  useEffect(() => {
    if (!pictureUrl || !picture) return;
    updatePicture({ fileUrl: pictureUrl, file: picture });
  }, [pictureUrl, picture, updatePicture]);

  return (
    <SettingsHeader {...rest}>
      <div className="grow">{children}</div>

      <Autosave
        isPending={isPendingUpdateOrganization || isPendingUpdatePicture}
        isSuccess={isSuccessUpdateOrganization || isSuccessUpdatePicture}
        error={errorUpdateOrganization ?? errorUpdatePicture}
      />
    </SettingsHeader>
  );
};

export const WorkspaceHeader = memo(IWorkspaceHeader) as typeof IWorkspaceHeader;
